import React, { useEffect, useMemo, useRef } from "react";
import { Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { useGenerateCampaignFormLinkedin } from "../../../../hooks/platforms/linkedin/useGenerateCampaignFormLinkedin";
import AnswerContainer from "../../../TypeForm/AnswerContainer";
import AnswerContainerTitleAndField from "../../../TypeForm/AnswerContainerTitleAndField";
import {
  Alert,
  FormControl,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  Radio,
  RadioGroup,
} from "@mui/material";
import AnswerTitle from "../../../TypeForm/AnswerTitle";
import AnswerSubTitle from "../../../TypeForm/AnswerSubtitle";
import ActionsButtons from "../../../TypeForm/ActionsButtons";
import { useTranslationApp } from "../../../../lib/i18next";
import Select from "../../../Form/Select";
import {
  optionsAdsCampaignObjectivesLinkedin,
  optionsAdsCampaignAdFormatLinkedin,
} from "../../../../utils/options";
import {
  SELECT_A_OPTION,
  AD_ACCOUNT_LINKEDIN_PLACEHOLDER,
  CREATE_CAMPAIGN_TRAFFIC_LINK_PLACEHOLDER,
  CREATE_ADS_CAMPAIGN_BUDGET_PLACEHOLDER,
  VALUE_EXPRESSED_IN,
  START_DATE_PLACEHOLDER,
  END_DATE_PLACEHOLDER,
  FIELD_REQUIRED,
  CREATE_ADS_CAMPAIGN_TYPE_BUDGET_DAILY_RADIO_LABEL,
  CREATE_ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_RADIO_LABEL,
  BUTTON_REMOVE,
} from "../../../../i18n/keysTranslations";
import {
  LINKEDIN,
  BORDER_RADIUS,
  FORMATS_MEDIA_LINKEDIN,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
  DOCUMENT_FORMATS_LINKEDIN,
  LINKEDIN_AD_FORMAT_DOCUMENT,
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_VIDEO_VIEWS_OPTION,
  linkedinCurrenciesWithDecimals,
} from "../../../../utils/constants";
import SelectBusinessAccount from "../../../Meta/SelectBusinessAccount";
import TextField from "../../../Form/TextField";
import NumberField from "../../../Form/NumberField";
import NumberDecimalField from "../../../Form/NumberDecimalField";
import DateField from "../../../Form/DateField";
import MediaUrlsAnalysisContainer from "../../../Common/MediaUrlsAnalysisContainer";
import {
  INPUT_COLOR_BORDER,
  BOX_SHADOW_FIELDS,
} from "../../../../utils/colors";
import _ from "lodash";
import {
  getDocumentDisplayName,
  getExtensionFile,
} from "../../../../utils/string";
import { addDays, startOfDay } from "date-fns";
import endOfDay from "date-fns/endOfDay";
import { useHydratePendingGalleryCreatives } from "../../../../hooks/useHydratePendingGalleryCreatives";
import LinkedinDocumentPreview from "../Common/LinkedinDocumentPreview";

const getLinkedInGalleryAcceptedFormats = (adFormat) => {
  if (adFormat === "SINGLE_VIDEO") {
    return VIDEO_FORMATS;
  }

  if (adFormat === LINKEDIN_AD_FORMAT_DOCUMENT) {
    return DOCUMENT_FORMATS_LINKEDIN;
  }

  if (adFormat === "STANDARD_UPDATE" || adFormat === "CAROUSEL") {
    return IMAGE_FORMATS;
  }

  if (adFormat) {
    return FORMATS_MEDIA_LINKEDIN;
  }

  return FORMATS_MEDIA_LINKEDIN.concat(VIDEO_FORMATS);
};

const DOCUMENT_ITEM_SIZE = 180;

const DocumentUrlsList = ({ mediaUrls = [], onRemoveItem }) => {
  const { t } = useTranslationApp();

  if (!mediaUrls.length) return null;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        overflowX: "auto",
        width: "100%",
        minWidth: 0,
        pb: 1,
      }}
    >
      {mediaUrls.map((mediaUrlData, index) => {
        const url = mediaUrlData?.url || "";
        const fileName = getDocumentDisplayName(url);
        const extension = getExtensionFile(url);

        return (
          <Box
            key={url || `document_${index}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              flex: "0 0 auto",
              width: DOCUMENT_ITEM_SIZE,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "1 / 1",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                borderRadius: 2,
                backgroundColor: "#F4F4F5",
                boxShadow: BOX_SHADOW_FIELDS,
                overflow: "hidden",
              }}
            >
              <LinkedinDocumentPreview
                url={url}
                fileName={fileName}
                extension={extension}
              />
              {extension && (
                <Box
                  sx={{
                    position: "absolute",
                    left: 8,
                    top: 8,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    backgroundColor: "rgba(0, 0, 0, 0.72)",
                    color: "#fff",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    lineHeight: 1.4,
                    pointerEvents: "none",
                    textTransform: "uppercase",
                  }}
                >
                  {extension}
                </Box>
              )}
              <IconButton
                onClick={() => onRemoveItem(index)}
                size="small"
                aria-label={t(BUTTON_REMOVE)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  width: 28,
                  height: 28,
                  backgroundColor: "white !important",
                  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.16)",
                  zIndex: 3,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              title={fileName}
              sx={{
                fontWeight: 600,
                px: 0.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fileName}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const GenerateCampaignFormLinkedin = ({
  initialData = {},
  campaignID,
  generatedPlatforms = {},
  context = {},
  galleryHydrationRef,
  onCallbackSave = () => {},
  onExitToPlatformSelection,
  verifyBlockAlert,
  resumeValues = null,
}) => {
  const config = useGenerateCampaignFormLinkedin({
    ...context,
    campaignID,
    generatedPlatforms,
    onCallbackSave,
    onExitToPlatformSelection,
    verifyBlockAlert,
    resumeValues,
  });
  const currentAnswer = config.currentAnswer;
  const fieldsConfig = config.fields;
  const { setValue, getValues, watch } = config.utils;
  const adFormat = watch("adFormat");
  const acceptedFormats = useMemo(
    () => getLinkedInGalleryAcceptedFormats(adFormat),
    [adFormat]
  );
  const steps = config.steps;
  const isLoadingDefaults = config.isLoadingDefaults;

  useEffect(() => {
    if (isLoadingDefaults || _.isEmpty(initialData)) return;

    Object.keys(initialData).forEach((key) => {
      if (key === "destinationURL" && initialData?.[key]) {
        setValue("destinationURL", initialData?.[key]);
      }
      if (
        key === "objective" &&
        initialData[key] &&
        optionsAdsCampaignObjectivesLinkedin.find(
          (option) => option.value === initialData?.[key]
        )
      ) {
        setValue("objective", initialData?.[key]);
      }
      if (
        key === "adFormat" &&
        initialData[key] &&
        optionsAdsCampaignAdFormatLinkedin.find(
          (option) => option.value === initialData?.[key]
        )
      ) {
        setValue("adFormat", initialData?.[key]);
      }
    });
    // eslint-disable-next-line
  }, [initialData, isLoadingDefaults]);

  useHydratePendingGalleryCreatives({
    initialData,
    isLoadingDefaults,
    currentAnswer,
    creativeStepName: "mediaUrls",
    fieldName: "mediaUrls",
    platformKey: LINKEDIN,
    galleryHydrationRef,
    setValue,
    getValues,
    acceptedFormats,
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 400,
        height: "100%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        {steps.map((step) => {
          const fieldConfig = fieldsConfig[step];
          if (!fieldConfig.isVisible || currentAnswer !== fieldConfig.name)
            return null;

          return (
            <CommonQuestionContainer
              key={fieldConfig.name}
              formConfig={config}
              fieldConfig={fieldConfig}
              context={context}
              campaignID={campaignID}
            />
          );
        })}
      </Box>
    </Box>
  );
};

const ObjectiveQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  return (
    <Select
      options={optionsAdsCampaignObjectivesLinkedin}
      name={fieldConfig.name}
      variant="filled"
      fullWidth
      label={t(SELECT_A_OPTION)}
      doTranslate={true}
      control={control}
    />
  );
};

const IdentitiesQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { setValue } = fieldConfig.utils;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
      }}
    >
      <SelectBusinessAccount
        variant="filled"
        label={t(AD_ACCOUNT_LINKEDIN_PLACEHOLDER)}
        control={control}
        platform={LINKEDIN}
        name="adAccountID"
        onChangeAction={(value) => {
          setValue("integrationID", value?.integrationID);
        }}
        fullWidth={true}
      />
    </Box>
  );
};

const DestinationURLQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  return (
    <TextField
      control={control}
      name={fieldConfig.name}
      fullWidth
      placeholder={t(CREATE_CAMPAIGN_TRAFFIC_LINK_PLACEHOLDER)}
      variant="filled"
    />
  );
};

const TypeBudgetQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { setValue } = fieldConfig.utils;

  return (
    <Controller
      control={control}
      name={fieldConfig.name}
      rules={{
        required: { value: true, message: t(FIELD_REQUIRED) },
      }}
      render={({ field, fieldState }) => (
        <FormControl
          component="fieldset"
          variant="standard"
          fullWidth
          error={!!fieldState.error}
        >
          <RadioGroup
            sx={{ gap: 1 }}
            value={field.value ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              field.onChange(value);
              if (value === ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION) {
                setValue("startDate", startOfDay(new Date()));
                setValue("endDate", null);
              }
            }}
          >
            <FormControlLabel
              value={ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION}
              control={<Radio />}
              label={t(CREATE_ADS_CAMPAIGN_TYPE_BUDGET_DAILY_RADIO_LABEL)}
            />
            <FormControlLabel
              value={ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION}
              control={<Radio />}
              label={t(CREATE_ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_RADIO_LABEL)}
            />
          </RadioGroup>
          {fieldState.error?.message ? (
            <FormHelperText>{fieldState.error.message}</FormHelperText>
          ) : null}
        </FormControl>
      )}
    />
  );
};

const DatesQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { watch, setValue } = fieldConfig.utils;
  const startDate = watch("startDate");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
      }}
    >
      <DateField
        sx={{
          backgroundColor: "fields.background",
          boxShadow: BOX_SHADOW_FIELDS,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          borderRadius: BORDER_RADIUS,
        }}
        variant="filled"
        name="startDate"
        label={t(START_DATE_PLACEHOLDER)}
        actionsChange={() => {
          setValue("endDate", null);
        }}
        control={control}
        minDate={startOfDay(new Date())}
        showStartNowButton={true}
      />
      <DateField
        sx={{
          backgroundColor: "fields.background",
          boxShadow: BOX_SHADOW_FIELDS,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          borderRadius: BORDER_RADIUS,
        }}
        variant="filled"
        name="endDate"
        defaultCalendarMonth={addDays(startDate || new Date(), 1)}
        actionsChange={(value) => {
          setValue(`endDate`, value ? endOfDay(value) : null);
        }}
        label={t(END_DATE_PLACEHOLDER)}
        control={control}
        minDate={addDays(startDate || new Date(), 1)}
      />
    </Box>
  );
};

const BudgetQuestion = ({ fieldConfig, control, currency }) => {
  const { t } = useTranslationApp();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: "100%",
      }}
    >
      <TextField
        control={control}
        name={fieldConfig.name}
        placeholder={t(CREATE_ADS_CAMPAIGN_BUDGET_PLACEHOLDER)}
        fullWidth
        variant="filled"
        InputProps={{
          inputComponent: linkedinCurrenciesWithDecimals.includes(currency)
            ? NumberDecimalField
            : NumberField,
        }}
        helperText={t(VALUE_EXPRESSED_IN, { currency })}
      />
    </Box>
  );
};

const AdFormatQuestion = ({ fieldConfig, control }) => {
  const { t } = useTranslationApp();
  const { setValue, watch } = fieldConfig.utils;
  const objective = watch("objective");
  const adFormatOptions =
    objective === ADS_CAMPAIGN_OBJECTIVE_VIDEO_VIEWS_OPTION
      ? optionsAdsCampaignAdFormatLinkedin.filter(
          (option) => option.value !== LINKEDIN_AD_FORMAT_DOCUMENT
        )
      : optionsAdsCampaignAdFormatLinkedin;
  return (
    <Select
      options={adFormatOptions}
      name={fieldConfig.name}
      variant="filled"
      fullWidth
      label={t(SELECT_A_OPTION)}
      doTranslate={true}
      control={control}
      actionsChange={() => {
        // Borrar mediaUrls cuando cambia el formato
        setValue("mediaUrls", []);
      }}
    />
  );
};

const MediaUrlsQuestion = ({ fieldConfig }) => {
  const { setValue, getValues, watch } = fieldConfig.utils;
  const mediaUrls = watch("mediaUrls") || [];
  const objective = watch("objective");
  const adFormat = watch("adFormat");
  const context = watch("context");
  const mediaUrlsAnalysisRef = useRef(null);
  const isDocumentFormat = adFormat === LINKEDIN_AD_FORMAT_DOCUMENT;

  const onCallbackCampaignMediaImport = ({ entries }) => {
    if (!entries?.length) return;
    const current = getValues("mediaUrls") || [];
    setValue("mediaUrls", [...current, ...entries]);
    requestAnimationFrame(() => {
      mediaUrlsAnalysisRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  // Use URL as identifier to avoid sync issues with async operations
  const onChangeItem = ({ url, data = {} }) => {
    if (!url) return; // Safety check
    const currentMediaUrls = getValues("mediaUrls") || [];
    const index = currentMediaUrls.findIndex((item) => item?.url === url);
    if (index === -1) return; // Element no longer exists, ignore update
    const currentData = currentMediaUrls[index];
    if (!currentData) return; // Safety check
    const newData = {
      ...currentData,
      ...data,
    };
    setValue(`mediaUrls.[${index}]`, newData);
  };

  const onRemoveItem = (index) => {
    setValue(
      "mediaUrls",
      mediaUrls.filter((_, i) => i !== index)
    );
  };

  if (isDocumentFormat) {
    return (
      <DocumentUrlsList mediaUrls={mediaUrls} onRemoveItem={onRemoveItem} />
    );
  }

  return (
    <MediaUrlsAnalysisContainer
      containerRef={mediaUrlsAnalysisRef}
      mediaUrls={mediaUrls}
      onRemoveItem={onRemoveItem}
      onChangeItem={onChangeItem}
      objective={objective}
      campaignContext={context}
      platform={LINKEDIN}
      disableAnalysis={isDocumentFormat}
      onCallbackCampaignMediaImport={onCallbackCampaignMediaImport}
    />
  );
};

const COMPONENTS = {
  objective: <ObjectiveQuestion />,
  identities: <IdentitiesQuestion />,
  destinationURL: <DestinationURLQuestion />,
  typeBudget: <TypeBudgetQuestion />,
  dates: <DatesQuestion />,
  budget: <BudgetQuestion />,
  adFormat: <AdFormatQuestion />,
  mediaUrls: <MediaUrlsQuestion />,
};

const CommonQuestionContainer = ({
  formConfig,
  fieldConfig,
  context,
  campaignID,
}) => {
  const { t } = useTranslationApp();

  const progress = formConfig.progress;
  const { control, setValue } = fieldConfig.utils;
  const error = formConfig.error;

  const COMPONENT = COMPONENTS[fieldConfig.name];

  if (!COMPONENT) return null;
  return (
    <>
      <LinearProgress
        sx={{
          width: "100%",
          mb: 3,
          maxWidth: 600,
          backgroundColor: "rgba(0,0,0,0.2)",
          "& .MuiLinearProgress-bar": { backgroundColor: "black" },
        }}
        variant="determinate"
        value={progress}
      />
      <AnswerContainer sx={{ display: "flex", height: "100%" }}>
        <AnswerContainerTitleAndField>
          {fieldConfig.title && (
            <AnswerTitle sx={{ mb: 1 }}>
              {`${t(fieldConfig.title, fieldConfig.titleExtrapolation)}`}
            </AnswerTitle>
          )}
          {fieldConfig.subtitle && (
            <AnswerSubTitle>
              {t(fieldConfig.subtitle, fieldConfig.subtitleExtrapolation)}
            </AnswerSubTitle>
          )}
          {fieldConfig.recommendedSizes && (
            <AnswerSubTitle>{t(fieldConfig.recommendedSizes)}</AnswerSubTitle>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 1,
                backgroundColor: "fields.background",
                color: "background.paperContrastText",
                border: `1px solid ${INPUT_COLOR_BORDER}`,
                borderRadius: BORDER_RADIUS,
                boxShadow: BOX_SHADOW_FIELDS,
              }}
            >
              {error}
            </Alert>
          )}
          {React.cloneElement(COMPONENT, {
            fieldConfig,
            control,
            setValue,
            error,
            currency: formConfig.currency,
            context,
            campaignID,
          })}
        </AnswerContainerTitleAndField>
        {fieldConfig.showActionsButtons && (
          <ActionsButtons
            onPreviousStep={fieldConfig.onPreviousStep}
            showPreviousButton={fieldConfig.showPreviousButton}
            showSkipButton={fieldConfig.showSkipButton}
            onSkipStep={fieldConfig.onSkipStep}
            onNextStep={fieldConfig.onNextStep}
            showNextButton={fieldConfig.showNextButton}
            nextButtonText={fieldConfig.nextButtonText}
            saveButtonText={fieldConfig.saveButtonText}
            showSaveButton={fieldConfig.isFinishQuestion}
            disabledSaveButton={fieldConfig.disabledFinishButton}
            onFinish={fieldConfig.onFinish}
            showLoadingComponent={fieldConfig.showLoadingComponent}
            customComponentPreviousNext={
              fieldConfig.customComponentPreviousNext
            }
            loading={formConfig.isCreating}
          />
        )}
      </AnswerContainer>
    </>
  );
};

export default GenerateCampaignFormLinkedin;
